import React, { useEffect, useState, useCallback } from 'react';
import { Form, Input, Radio, Typography } from 'antd';
import styled from 'styled-components';
import { get } from 'lodash';
import container from "container/BrokerDashboard/editAgentContainer";
import {
  GoogleAutoComplete,
} from "dumbComponents/common/UI/antd"

const { Title } = Typography;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const WideField = styled(Form.Item)`
  grid-column: span 2;
`;

const RadioRow = styled(Radio.Group)`
  display: flex;
  gap: 32px;
`;

const Box = styled.div`
  border: 1px solid #e0e0e0;
  padding: 16px 24px;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 16px;
`;

const DbaNameContainer = styled.div`
  margin-bottom: 16px;
  margin-left: -5px;
`;

const StyledFormItem = styled(Form.Item)`
  .ant-form-item-label > label {
    color: #262626;
    font-weight: 500;
    font-size: 14px;
  }
  
  .ant-input {
    border: 1px solid #d9d9d9;
    border-radius: 8px !important;
    background: #ffffff;
    color: #262626;
    height: 40px !important;
    
    &:hover {
      border-color: #bfbfbf;
    }
    
    &:focus {
      border-color: #722ed1;
      box-shadow: 0 0 0 2px rgba(114, 46, 209, 0.1);
    }
  }
  
  .ant-input[disabled] {
    background: #ffffff;
    color: #262626;
    border-color: #d9d9d9;
  }
  
  .ant-form-item-control {
    margin-bottom: 0 !important;
  }
  
  margin-bottom: 0 !important;
`;

const DbaSection = ({ disabled, getOnboardingDbaInfoResponse, onPayloadReady, agentId, agentRole }) => {
  const { data: dbaData } = getOnboardingDbaInfoResponse || {};
  const [form] = Form.useForm();
  
  const [sameAddress, setSameAddress] = useState('yes');
  const [dbaStatus, setDbaStatus] = useState('new');

  const isRoleAllowed = () => {
    const allowedRoles = ['team_lead', 'co_team_lead', 'admin_agent', 'operation_agent', 'individual_agent'];
    return allowedRoles.includes(agentRole?.toLowerCase());
  };

  const shouldDisableFields = disabled || !isRoleAllowed();

  // Handle DBA address change
  const handleDbaAddressChange = useCallback((e) => {
    const value = e.target.value;
    form.setFieldsValue({ dbaAddress: value });
  }, [form]);

  // Handle DBA address selection from Google
  const handleDbaAddressSelect = useCallback((location) => {
    console.log('Google DBA location selected:', location);

    
    // Helper function to extract state from address string
    const extractStateFromAddress = (addressStr) => {
      if (!addressStr) return '';
      
      // Split by comma and look for state pattern (2-3 letter code)
      const parts = addressStr.split(',').map(part => part.trim());
      
      // Look for state code pattern (2-3 letters, possibly followed by zip)
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        // Match state pattern: 2-3 letters, optionally followed by zip
        const stateMatch = part.match(/^([A-Z]{2,3})(\s+\d{5}(-\d{4})?)?$/);
        if (stateMatch) {
          return stateMatch[1];
        }
      }
      return '';
    };

    // Helper function to extract city from address string
    const extractCityFromAddress = (addressStr) => {
      if (!addressStr) return '';
      
      const parts = addressStr.split(',').map(part => part.trim());
      
      // City is typically the second part (after street address)
      if (parts.length >= 2) {
        return parts[1];
      }
      return '';
    };

    // Helper function to extract zipcode from address string
    const extractZipcodeFromAddress = (addressStr) => {
      if (!addressStr) return '';
      
      const parts = addressStr.split(',').map(part => part.trim());
      
      // Look for zipcode pattern in any part
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const zipMatch = part.match(/\d{5}(-\d{4})?/);
        if (zipMatch) {
          return zipMatch[0];
        }
      }
      return '';
    };

    // Build street address
    const streetNumber = location?.street_number?.long_name || '';
    const route = location?.route?.long_name || '';
    const streetAddress = `${streetNumber} ${route}`.trim();

    // Extract state with fallback logic
    let state = '';
    if (location?.administrative_area_level_1?.long_name) {
      state = location.administrative_area_level_1.long_name;
    } else if (location?.address) {
      state = extractStateFromAddress(location.address);
    }

    // Extract city with fallback logic
    let city = '';
    if (location?.locality?.long_name) {
      city = location.locality.long_name;
    } else if (location?.address) {
      city = extractCityFromAddress(location.address);
    }

    // Extract zipcode with fallback logic
    let zipcode = '';
    if (location?.postal_code?.long_name) {
      zipcode = location.postal_code.long_name;
    } else if (location?.postal_code?.short_name) {
      zipcode = location.postal_code.short_name;
    } else if (location?.address) {
      zipcode = extractZipcodeFromAddress(location.address);
    }

    const selectedAddress = {
      address: streetAddress,
      city: city,
      state: state,
      zipcode: zipcode,
    };


    // Update form with the selected address data
    form.setFieldsValue({
      dbaAddress: selectedAddress.address,
      dbaCity: selectedAddress.city,
      dbaState: selectedAddress.state,
      dbaZip: selectedAddress.zipcode,
    });

  }, [form]);

  // Create DBA payload and send to parent
  const createDbaPayload = () => {
    const values = form.getFieldsValue();
    
    const dbaPayload = {
      data: {
        active: 0,
        dba_name: values.dbaName || "",
        city: values.dbaCity || "",
        apt: values.dbaSuite || "",
        state: values.dbaState || "",
        zipcode: values.dbaZip || "",
        address: values.dbaAddress || "",
        is_dba_address_same_as_office_address: values.sameAddress === "yes",
        new_registration: values.dbaStatus === "new" ? "new_registered" : "prev_registered",
        require_branch_office_viewable_in_mls: true,
        corresponding_entity: values.hasEntity === "yes",
        own_brand_assets: values.hasBrandAssets === "yes",
        old_dba_county: values.dbaCounty || "",
        who_owns_this_dba: values.dbaOwner || "",
      },
      step_completed: "dba_info",
      agent_id: parseInt(agentId),
    };
    
    // Send payload to parent component
    if (onPayloadReady) {
      onPayloadReady(dbaPayload);
    }
    
    return dbaPayload;
  };

  // Expose createDbaPayload function to parent
  useEffect(() => {
    if (onPayloadReady) {
      // Expose the function to parent component
      onPayloadReady({ createDbaPayload });
    }
  }, [onPayloadReady]);

  useEffect(() => {
    if (dbaData) {
      const formValues = {
        dbaName: dbaData?.dba_name || '',
        dbaStatus: dbaData?.new_registration === 1 ? 'new' : 'previous',
        dbaCounty: dbaData?.old_dba_county || '',
        dbaOwner: dbaData?.dba_owner || '',
        hasEntity: dbaData?.corresponding_entity === 1 ? 'yes' : 'no',
        hasBrandAssets: dbaData?.own_brand_assets === 1 ? 'yes' : 'no',
        sameAddress: dbaData?.is_dba_address_same_as_office_address === 1 ? 'yes' : 'no',
        dbaAddress: dbaData?.address || '',
        dbaSuite: dbaData?.apt || '',
        dbaState: dbaData?.state || '',
        dbaZip: dbaData?.zipcode || '',
        dbaCity: dbaData?.city || '',
      };
      
      form.setFieldsValue(formValues);
      setDbaStatus(formValues.dbaStatus);
      setSameAddress(formValues.sameAddress);
    }
  }, [dbaData, form]);

  // Method to get payload - includes all values when editable
  const getPayload = () => {
    const values = form.getFieldsValue();
    
    // When disabled (read-only), return current form values
    if (disabled) {
      return {
        dba_name: values.dbaName,
        new_registration: values.dbaStatus === 'new' ? 1 : 0,
        old_dba_county: values.dbaStatus === 'previous' ? values.dbaCounty : null,
        dba_owner: values.dbaStatus === 'previous' ? values.dbaOwner : null,
        corresponding_entity: values.hasEntity === 'yes' ? 1 : 0,
        own_brand_assets: values.hasBrandAssets === 'yes' ? 1 : 0,
        is_dba_address_same_as_office_address: values.sameAddress === 'yes' ? 1 : 0,
        address: values.sameAddress === 'no' ? values.dbaAddress : null,
        apt: values.sameAddress === 'no' ? values.dbaSuite : null,
        state: values.sameAddress === 'no' ? values.dbaState : null,
        zipcode: values.sameAddress === 'no' ? values.dbaZip : null,
        city: values.sameAddress === 'no' ? values.dbaCity : null,
      };
    }
    
    // When editable, include all values regardless of conditions
    return {
      dba_name: values.dbaName,
      new_registration: values.dbaStatus === 'new' ? 1 : 0,
      old_dba_county: values.dbaCounty,
      dba_owner: values.dbaOwner,
      corresponding_entity: values.hasEntity === 'yes' ? 1 : 0,
      own_brand_assets: values.hasBrandAssets === 'yes' ? 1 : 0,
      is_dba_address_same_as_office_address: values.sameAddress === 'yes' ? 1 : 0,
      address: values.dbaAddress,
      apt: values.dbaSuite,
      state: values.dbaState,
      zipcode: values.dbaZip,
      city: values.dbaCity,
    };
  };

  return (
    <Form layout="vertical" form={form}>
      <Box>
        <Title level={5} style={{ marginBottom: 8, fontWeight: 500, fontSize: 16, marginLeft: "-2px"}}>DBA Details</Title>
      <DbaNameContainer>
        <StyledFormItem name="dbaName" label="DBA name">
          <Input placeholder="" disabled={shouldDisableFields} />
        </StyledFormItem>
      </DbaNameContainer>

      <Box>
        <StyledFormItem label="Is DBA new or previously registered?" name="dbaStatus">
          <RadioRow
            onChange={(e) => setDbaStatus(e.target.value)}
            value={dbaStatus}
            disabled={shouldDisableFields}
          >
            <Radio value="new">New registered</Radio>
            <Radio value="previous">Previously registered</Radio>
          </RadioRow>
        </StyledFormItem>

        {dbaStatus === 'previous' && (
          <Grid style={{ marginTop: 12 }}>
            <StyledFormItem label="Where was it registered?" name="dbaCounty">
              <Input placeholder="" disabled={shouldDisableFields} />
            </StyledFormItem>
            <StyledFormItem label="Who owns this DBA?" name="dbaOwner">
              <Input placeholder="" disabled={shouldDisableFields} />
            </StyledFormItem>
          </Grid>
        )}
      </Box>

      <Box>
        <StyledFormItem
          label="Does business have a corresponding entity, e.g., S corp, LLC, etc.?"
          name="hasEntity"
        >
          <RadioRow disabled={shouldDisableFields}>
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </RadioRow>
        </StyledFormItem>
      </Box>

      <Box>
        <StyledFormItem
          label="Does business have its own brand assets and Corp ID/logo?"
          name="hasBrandAssets"
        >
          <RadioRow disabled={shouldDisableFields}>
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </RadioRow>
        </StyledFormItem>
      </Box>

      <Box>
        <StyledFormItem
          label="Does business use the same address mentioned earlier?"
          name="sameAddress"
        >
          <RadioRow
            value={sameAddress}
            onChange={(e) => setSameAddress(e.target.value)}
            disabled={shouldDisableFields}
          >
            <Radio value="yes">Yes</Radio>
            <Radio value="no">No</Radio>
          </RadioRow>
        </StyledFormItem>

        {sameAddress === 'no' && (
          <Box>
            <Grid style={{ marginTop: 12 }}>
              <StyledFormItem label="Additional office address" name="dbaAddress">
                <Form.Item name="dbaAddress" noStyle>
                  <GoogleAutoComplete
                    id="dba-address"
                    types={["address"]}
                    name="dba-address"
                    onChange={handleDbaAddressChange}
                    location={handleDbaAddressSelect}
                    placeholder=""
                    disabled={shouldDisableFields}
                    styles={{
                      height: "40px",
                      borderRadius: "8px",
                      background: "white",
                      fontSize: "14px",
                      border: "1px solid #d9d9d9",
                      padding: "8px 12px",
                    }}
                    restrict={{
                      lat: 37.09024,
                      lng: -95.712891,
                      south_west: {
                        lat: 25.82,
                        lng: -124.39,
                      },
                      north_east: {
                        lat: 49.38,
                        lng: -66.94,
                      },
                      address: "United States",
                      country: {
                        long_name: "United States",
                        short_name: "US",
                        types: [
                          "country",
                          "political",
                        ],
                      },
                    }}
                  />
                </Form.Item>
              </StyledFormItem>
              <StyledFormItem label="Apt/unit/suite no." name="dbaSuite">
                <Input placeholder="" disabled={shouldDisableFields} />
              </StyledFormItem>
              <StyledFormItem label="City" name="dbaCity">
                <Input placeholder="" disabled={shouldDisableFields} />
              </StyledFormItem>
              <StyledFormItem label="State" name="dbaState">
                <Input placeholder="" disabled={shouldDisableFields} />
              </StyledFormItem>
              <StyledFormItem label="Zipcode" name="dbaZip">
                <Input placeholder="" disabled={shouldDisableFields} />
              </StyledFormItem>
            </Grid>
          </Box>
        )}
      </Box>
      </Box>
    </Form>
  );
};

export default container(DbaSection);
